import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BOPHAN } from '../constants';
import { Get, Post, Put, DeleteSingle } from '../../utils/httpProvider';
import { BOPHAN_API } from '../../utils/apiUrl';

export const boPhanGetAllAction = createAsyncThunk(
    BOPHAN.GET_ALL,
    async (_, { rejectWithValue }) => {
        try {
            const data = await Get(BOPHAN_API.BASE, null, null);
            if (!data) return rejectWithValue('Không lấy được dữ liệu Bộ phận');
            return data;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const boPhanCreateAction = createAsyncThunk(
    BOPHAN.CREATE,
    async (model, { rejectWithValue }) => {
        try {
            const res = await Post(BOPHAN_API.CREATE, null, model, false);

            if (res.status === 200 || res.status === 201) {
                const body = await res.json(); // đọc body 1 lần
                return body;
            }

            let errBody = null;
            try {
                errBody = await res.json(); // đọc body lỗi (1 lần cho nhánh lỗi)
            } catch (e) {
                // nếu không parse được thì bỏ qua
            }
            return rejectWithValue(errBody || 'Tạo bộ phận thất bại');
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

/**
 * UPDATE – phần gây lỗi trước đây.
 * Sửa lại để:
 *  - Ưu tiên dùng status của Response để quyết định thành công/thất bại.
 *  - Không cố đọc body JSON nếu không cần (tránh lỗi "body stream already read").
 */
export const boPhanUpdateAction = createAsyncThunk(
    BOPHAN.UPDATE,
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const res = await Put(BOPHAN_API.UPDATE, id, data);

            // Trường hợp Put trả về Response (thông thường)
            if (res && typeof res.status === 'number') {
                // Thành công: 2xx
                if (res.status >= 200 && res.status < 300) {
                    // KHÔNG gọi res.json() nữa để tránh đọc lại stream
                    // Trả về object tối thiểu để cập nhật list trên FE
                    return { id, ...data };
                }

                // Thất bại: cố gắng đọc body lỗi, nhưng nuốt lỗi stream nếu có
                let errBody = null;
                try {
                    errBody = await res.json();
                } catch (e) {
                    // nếu "body stream already read" thì bỏ qua, dùng message chung
                }
                return rejectWithValue(errBody || 'Cập nhật bộ phận thất bại');
            }

            // Trường hợp Put đã trả sẵn data (không phải Response)
            if (!res) {
                return rejectWithValue('Cập nhật bộ phận thất bại');
            }
            return res;
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

export const boPhanDeleteAction = createAsyncThunk(
    BOPHAN.DELETE,
    async (id, { rejectWithValue }) => {
        try {
            const res = await DeleteSingle(BOPHAN_API.DELETE, id, null);
            if (res.status === 200 || res.status === 204) {
                return id;
            }
            let errBody = null;
            try {
                errBody = await res.json();
            } catch (e) {
                // không đọc được body thì dùng message chung
            }
            return rejectWithValue(errBody || 'Xoá bộ phận thất bại');
        } catch (err) {
            return rejectWithValue(err);
        }
    }
);

const initialState = {
    list: [],
    loading: false,
    error: null,
};

export const boPhanSlice = createSlice({
    name: BOPHAN.NAME_SLICE,
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // ===== GET ALL =====
            .addCase(boPhanGetAllAction.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(boPhanGetAllAction.fulfilled, (state, action) => {
                state.loading = false;
                state.list = action.payload || [];
            })
            .addCase(boPhanGetAllAction.rejected, (state, action) => {
                state.loading = false;
                state.error =
                    action.payload ||
                    (action.error && action.error.message) ||
                    'Lỗi khi tải danh sách bộ phận';
            })

            // ===== CREATE =====
            .addCase(boPhanCreateAction.fulfilled, (state, action) => {
                if (action.payload) {
                    state.list.push(action.payload);
                }
            })
            .addCase(boPhanCreateAction.rejected, (state, action) => {
                state.error =
                    action.payload ||
                    (action.error && action.error.message) ||
                    'Tạo bộ phận thất bại';
            })

            // ===== UPDATE =====
            .addCase(boPhanUpdateAction.fulfilled, (state, action) => {
                const updated = action.payload;
                if (!updated) return;
                const idx = state.list.findIndex((x) => x.id === updated.id);
                if (idx >= 0) {
                    state.list[idx] = updated;
                }
            })
            .addCase(boPhanUpdateAction.rejected, (state, action) => {
                state.error =
                    action.payload ||
                    (action.error && action.error.message) ||
                    'Cập nhật bộ phận thất bại';
            })

            // ===== DELETE =====
            .addCase(boPhanDeleteAction.fulfilled, (state, action) => {
                const id = action.payload;
                state.list = state.list.filter((x) => x.id !== id);
            })
            .addCase(boPhanDeleteAction.rejected, (state, action) => {
                state.error =
                    action.payload ||
                    (action.error && action.error.message) ||
                    'Xoá bộ phận thất bại';
            });
    },
});

export default boPhanSlice.reducer;
